import dayGridPlugin from '@fullcalendar/daygrid';
import interactionPlugin from '@fullcalendar/interaction';
import FullCalendar from '@fullcalendar/react';
import { Card, CardContent, Dialog } from '@mui/material';
import { LocalizationProvider, StaticDatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Layout } from 'components/layout';
import dayjs, { Dayjs } from 'dayjs';
import { useRef, useState } from 'react';
import AddWorkEventDialog from '../components/AddWorkEventDialog';
import { EventClickArg } from '@fullcalendar/core';
import { useCreateCurrentYearHolidayApiMutation, useGetAllWorkCalendarApiQuery } from 'lib/api/admin/adminWorkCalendar';
import { format, set } from 'date-fns';
import DetailWorkEventDialog from '../components/DetailWorkEventDialog';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';

interface IWorkEvent {
    id: string;
    title: string;
    date: string;
}

const AdminWorkCalendar = () => {
    const calendarRef = useRef<FullCalendar>(null);
    const [datepickerVisible, setDatepickerVisible] = useState(false);
    const [openAddWorkEventDialog, setOpenAddWorkEventDialog] = useState(false);
    const [openDetailWorkEventDialog, setOpenDetailWorkEventDialog] = useState<{ id: string | null; open: boolean }>({
        id: null,
        open: false
    });
    const [selectedDate, setSelectedDate] = useState<{ selectedMonth: Dayjs; fromDate: Dayjs; toDate: Dayjs }>({
        selectedMonth: dayjs(),
        fromDate: dayjs(),
        toDate: dayjs()
    });
    const [clickedDate, setClickedDate] = useState<Date | null>(null);
    const [generateCurrentYearMutation] = useCreateCurrentYearHolidayApiMutation();
    const { data: workEvents, isLoading: workEventsIsLoading } = useGetAllWorkCalendarApiQuery(
        {
            fromDate: selectedDate.fromDate.startOf('month').subtract(1, 'month').format('YYYY-MM-DD'),
            toDate: selectedDate.toDate.endOf('month').add(1, 'month').format('YYYY-MM-DD')
        },
        {
            refetchOnMountOrArgChange: true,
            refetchOnFocus: true
        }
    );

    const handleDateChange = (date: Dayjs | null) => {
        if (date) {
            setSelectedDate(() => ({
                selectedMonth: date.date(15),
                fromDate: date.startOf('month').subtract(1, 'month'),
                toDate: date.endOf('month').add(1, 'month')
            }));
            calendarRef.current?.getApi().gotoDate(date.startOf('month').toDate());
        }
        setDatepickerVisible(false);
    };

    const handleEventClick = (clickInfo: EventClickArg) => {
        setOpenDetailWorkEventDialog({
            id: clickInfo.event.id,
            open: true
        });
    };

    return (
        <>
            <Layout headerTitle="Holiday Calendar" pageTitle="Holiday Calendar" disableBreadcrumb>
                <Card>
                    <CardContent>
                        <FullCalendar
                            ref={calendarRef}
                            plugins={[dayGridPlugin, interactionPlugin]}
                            initialView="dayGridMonth"
                            eventBackgroundColor="#A52A2A"
                            events={workEvents?.data?.map((event: IWorkEvent) => ({
                                id: event.id,
                                title: event.title,
                                date: format(new Date(event.date), 'yyyy-MM-dd')
                            }))}
                            dateClick={(arg) => {
                                setClickedDate(arg.date);
                                setOpenAddWorkEventDialog(true);
                            }}
                            eventClick={handleEventClick}
                            headerToolbar={{
                                left: 'title',
                                right: 'generateHoliday selectMonthButton'
                            }}
                            customButtons={{
                                generateHoliday: {
                                    text: 'Generate Holiday',
                                    click: async () => {
                                        await generateCurrentYearMutation(null)
                                            .unwrap()
                                            .then(() => {
                                                withReactContent(Swal).fire({
                                                    title: 'Success',
                                                    text: 'Successfully generated holidays for the current year.',
                                                    icon: 'success'
                                                });
                                            });
                                    }
                                },
                                selectMonthButton: {
                                    text: 'Select Month',
                                    click: () => {
                                        setDatepickerVisible(!datepickerVisible);
                                    }
                                }
                            }}
                        />
                    </CardContent>
                </Card>
            </Layout>
            {openAddWorkEventDialog && clickedDate && (
                <AddWorkEventDialog
                    initialDate={clickedDate}
                    open={openAddWorkEventDialog}
                    handleClose={() => setOpenAddWorkEventDialog(false)}
                />
            )}
            {openDetailWorkEventDialog.open && openDetailWorkEventDialog.id && (
                <DetailWorkEventDialog
                    id={openDetailWorkEventDialog.id}
                    open={openDetailWorkEventDialog.open}
                    handleClose={() =>
                        setOpenDetailWorkEventDialog({
                            id: null,
                            open: false
                        })
                    }
                />
            )}
            {datepickerVisible && (
                <Dialog open={datepickerVisible} onClose={() => setDatepickerVisible(false)}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        <StaticDatePicker
                            displayStaticWrapperAs="desktop"
                            value={selectedDate.selectedMonth}
                            onChange={(newValue) => {
                                if (newValue) {
                                    handleDateChange(newValue);
                                }
                            }}
                            views={['month', 'year']}
                        />
                    </LocalizationProvider>
                </Dialog>
            )}
        </>
    );
};

export default AdminWorkCalendar;
